/**
 * LOGIN PAGE - FLO SIGNATURE DESIGN SYSTEM v2
 * Clean. Controlled. Consistent. Light ops mode.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { EyeIcon, EyeSlashIcon, LockClosedIcon, UserIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { COLORS, SPACING, TYPOGRAPHY, RADIUS } from '../design/tokens';
import { Panel, Button, Screen } from '../design/primitives';
import { Alert } from '../components/ui';

const LoginPage: React.FC = () => {
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    rememberMe: false
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const navigate = useNavigate();
  const { login, isAuthenticated } = useAuth();

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.username) {
      newErrors.username = 'Username is required';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      const result = await login(formData.username, formData.password);

      if (result.success) {
        // Login successful, redirect to dashboard
        navigate('/', { replace: true });
      } else {
        // Login failed, show error
        setErrors({ general: result.error || 'Login failed. Please try again.' });
      }
    } catch (error) {
      console.error('Login error:', error);
      setErrors({ general: 'An unexpected error occurred. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Screen>
      <div
        style={{
          minHeight: 'calc(100vh - 48px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
      <div
        style={{
          maxWidth: '448px',
          width: '100%',
        }}
      >
        {/* Header */}
        <div
          style={{
            textAlign: 'center',
            marginBottom: SPACING[32],
          }}
        >
          <div
            style={{
              margin: '0 auto',
              height: '48px',
              width: '48px',
              backgroundColor: COLORS.accent.default,
              borderRadius: RADIUS,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: SPACING[24],
            }}
          >
            <LockClosedIcon
              style={{
                height: '24px',
                width: '24px',
                color: '#FFFFFF',
              }}
            />
          </div>
          <h2
            style={{
              fontFamily: TYPOGRAPHY.headings.fontFamily,
              fontSize: TYPOGRAPHY.size['5xl'],
              fontWeight: TYPOGRAPHY.headings.weight,
              color: COLORS.text.primary,
              margin: 0,
              marginBottom: SPACING[8],
            }}
          >
            Sign in to FloOS
          </h2>
          <p
            style={{
              fontFamily: TYPOGRAPHY.body.fontFamily,
              fontSize: TYPOGRAPHY.body.sizes.default,
              color: COLORS.text.secondary,
              margin: 0,
            }}
          >
            Welcome back to your personal operating system
          </p>
        </div>

        {/* Form */}
        <Panel>
          <form
            onSubmit={handleSubmit}
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: SPACING[24],
            }}
          >
            {errors.general && (
              <Alert variant="error">{errors.general}</Alert>
            )}

            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: SPACING[16],
              }}
            >
              {/* Username Field */}
              <div>
                <label
                  htmlFor="username"
                  style={{
                    display: 'block',
                    fontFamily: TYPOGRAPHY.body.fontFamily,
                    fontSize: TYPOGRAPHY.body.sizes.default,
                    fontWeight: '500',
                    color: COLORS.text.primary,
                    marginBottom: SPACING[4],
                  }}
                >
                  Username
                </label>
                <div style={{ position: 'relative', marginTop: SPACING[4] }}>
                  <div
                    style={{
                      position: 'absolute',
                      top: 0,
                      bottom: 0,
                      left: SPACING[12],
                      display: 'flex',
                      alignItems: 'center',
                      pointerEvents: 'none',
                    }}
                  >
                    <UserIcon
                      style={{
                        height: '20px',
                        width: '20px',
                        color: COLORS.text.tertiary,
                      }}
                    />
                  </div>
                  <input
                    id="username"
                    name="username"
                    type="text"
                    autoComplete="username"
                    required
                    value={formData.username}
                    onChange={handleInputChange}
                    placeholder="Enter your username"
                    style={{
                      appearance: 'none',
                      position: 'relative',
                      display: 'block',
                      width: '100%',
                      paddingLeft: SPACING[40],
                      paddingRight: SPACING[12],
                      paddingTop: SPACING[8],
                      paddingBottom: SPACING[8],
                      border: `1px solid ${errors.username ? COLORS.status.error : COLORS.border.default}`,
                      fontFamily: TYPOGRAPHY.body.fontFamily,
                      fontSize: TYPOGRAPHY.body.sizes.default,
                      color: COLORS.text.primary,
                      backgroundColor: COLORS.bg.panel,
                      borderRadius: RADIUS,
                      outline: 'none',
                      transition: 'border-color 0.15s ease',
                    }}
                    onFocus={(e) => (e.target.style.borderColor = errors.username ? COLORS.status.error : COLORS.accent.default)}
                    onBlur={(e) => (e.target.style.borderColor = errors.username ? COLORS.status.error : COLORS.border.default)}
                  />
                </div>
                {errors.username && (
                  <p
                    style={{
                      fontFamily: TYPOGRAPHY.body.fontFamily,
                      fontSize: TYPOGRAPHY.body.sizes.small,
                      color: COLORS.status.error,
                      margin: 0,
                      marginTop: SPACING[4],
                    }}
                  >
                    {errors.username}
                  </p>
                )}
              </div>

              {/* Password Field */}
              <div>
                <label
                  htmlFor="password"
                  style={{
                    display: 'block',
                    fontFamily: TYPOGRAPHY.body.fontFamily,
                    fontSize: TYPOGRAPHY.body.sizes.default,
                    fontWeight: '500',
                    color: COLORS.text.primary,
                    marginBottom: SPACING[4],
                  }}
                >
                  Password
                </label>
                <div style={{ position: 'relative', marginTop: SPACING[4] }}>
                  <div
                    style={{
                      position: 'absolute',
                      top: 0,
                      bottom: 0,
                      left: SPACING[12],
                      display: 'flex',
                      alignItems: 'center',
                      pointerEvents: 'none',
                    }}
                  >
                    <LockClosedIcon
                      style={{
                        height: '20px',
                        width: '20px',
                        color: COLORS.text.tertiary,
                      }}
                    />
                  </div>
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    required
                    value={formData.password}
                    onChange={handleInputChange}
                    placeholder="Enter your password"
                    style={{
                      appearance: 'none',
                      position: 'relative',
                      display: 'block',
                      width: '100%',
                      paddingLeft: SPACING[40],
                      paddingRight: SPACING[40],
                      paddingTop: SPACING[8],
                      paddingBottom: SPACING[8],
                      border: `1px solid ${errors.password ? COLORS.status.error : COLORS.border.default}`,
                      fontFamily: TYPOGRAPHY.body.fontFamily,
                      fontSize: TYPOGRAPHY.body.sizes.default,
                      color: COLORS.text.primary,
                      backgroundColor: COLORS.bg.panel,
                      borderRadius: RADIUS,
                      outline: 'none',
                      transition: 'border-color 0.15s ease',
                    }}
                    onFocus={(e) => (e.target.style.borderColor = errors.password ? COLORS.status.error : COLORS.accent.default)}
                    onBlur={(e) => (e.target.style.borderColor = errors.password ? COLORS.status.error : COLORS.border.default)}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    style={{
                      position: 'absolute',
                      top: 0,
                      bottom: 0,
                      right: SPACING[12],
                      display: 'flex',
                      alignItems: 'center',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: 0,
                    }}
                  >
                    {showPassword ? (
                      <EyeSlashIcon
                        style={{
                          height: '20px',
                          width: '20px',
                          color: COLORS.text.tertiary,
                          transition: 'color 0.15s ease',
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.color = COLORS.text.secondary)}
                        onMouseLeave={(e) => (e.currentTarget.style.color = COLORS.text.tertiary)}
                      />
                    ) : (
                      <EyeIcon
                        style={{
                          height: '20px',
                          width: '20px',
                          color: COLORS.text.tertiary,
                          transition: 'color 0.15s ease',
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.color = COLORS.text.secondary)}
                        onMouseLeave={(e) => (e.currentTarget.style.color = COLORS.text.tertiary)}
                      />
                    )}
                  </button>
                </div>
                {errors.password && (
                  <p
                    style={{
                      fontFamily: TYPOGRAPHY.body.fontFamily,
                      fontSize: TYPOGRAPHY.body.sizes.small,
                      color: COLORS.status.error,
                      margin: 0,
                      marginTop: SPACING[4],
                    }}
                  >
                    {errors.password}
                  </p>
                )}
              </div>
            </div>

            {/* Remember Me */}
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <input
                id="remember-me"
                name="rememberMe"
                type="checkbox"
                checked={formData.rememberMe}
                onChange={handleInputChange}
                style={{
                  height: '16px',
                  width: '16px',
                  color: COLORS.accent.default,
                  borderColor: COLORS.border.default,
                  borderRadius: RADIUS,
                  cursor: 'pointer',
                }}
              />
              <label
                htmlFor="remember-me"
                style={{
                  marginLeft: SPACING[8],
                  display: 'block',
                  fontFamily: TYPOGRAPHY.body.fontFamily,
                  fontSize: TYPOGRAPHY.body.sizes.default,
                  color: COLORS.text.primary,
                  cursor: 'pointer',
                }}
              >
                Remember me
              </label>
            </div>

            {/* Submit Button */}
            <div>
              <button
                type="submit"
                disabled={isLoading}
                style={{
                  position: 'relative',
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  paddingTop: SPACING[8],
                  paddingBottom: SPACING[8],
                  paddingLeft: SPACING[16],
                  paddingRight: SPACING[16],
                  border: 'none',
                  fontFamily: TYPOGRAPHY.body.fontFamily,
                  fontSize: TYPOGRAPHY.body.sizes.default,
                  fontWeight: '500',
                  borderRadius: RADIUS,
                  color: '#FFFFFF',
                  backgroundColor: COLORS.accent.default,
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  opacity: isLoading ? 0.5 : 1,
                  outline: 'none',
                  transition: 'background-color 0.2s ease',
                }}
                onMouseEnter={(e) => !isLoading && (e.currentTarget.style.backgroundColor = COLORS.accent.hover)}
                onMouseLeave={(e) => !isLoading && (e.currentTarget.style.backgroundColor = COLORS.accent.default)}
                onFocus={(e) => !isLoading && (e.currentTarget.style.boxShadow = `0 0 0 3px rgba(59, 130, 246, 0.2)`)}
                onBlur={(e) => (e.currentTarget.style.boxShadow = 'none')}
              >
                {isLoading ? (
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div
                      style={{
                        animation: 'spin 1s linear infinite',
                        borderRadius: '50%',
                        height: '16px',
                        width: '16px',
                        borderTop: '2px solid #FFFFFF',
                        borderRight: '2px solid transparent',
                        marginRight: SPACING[8],
                      }}
                    ></div>
                    Signing in...
                  </div>
                ) : (
                  'Sign in'
                )}
              </button>
            </div>
          </form>
        </Panel>
      </div>

      {/* CSS Animation for loading spinner */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
      </div>
    </Screen>
  );
};

export default LoginPage;
